import {Inject, Injectable, Optional} from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {Observable, of, ReplaySubject, throwError} from 'rxjs';
import {catchError, filter, switchMap} from 'rxjs/operators';
import {DOCUMENT} from '@angular/common';
import Cropper from 'cropperjs';
import {ImageService} from '@ui-app/services/image.service';
import {AppToastrService} from '@ui-app/services/toastr.service';
import {IAllCropperData, ICropBoxViewData} from '../../../../DBadgeEmployee-app/src/app/modals/photo-review-modal/cropper.models';
import {API_CONFIG, ICroppingSettings, ICroppPhotoRequestOld, IDetectFacesDto} from '@shared-models/common.models';
import {EReturnImageResizeType, ResponseStatus} from '@shared-models/common.enums';
import {FileUploadService} from '@ui-app/components/file-upload/file-upload.service';

export interface IAutoCropResultData {
    croppedImageBase64: string;
    cropperData: IAllCropperData;
    isImageRecognitionEnabled: boolean;
}

let ApiConfig;

@Injectable({
    providedIn: 'root'
})
export class AutomaticCroppingService {
    cropper: Cropper;

    constructor(@Inject(DOCUMENT) private document: Document,
                private httpClient: HttpClient,
                private toasterService: AppToastrService,
                private imageService: ImageService,
                @Optional() @Inject(API_CONFIG) public apiConfig: string,
                @Optional() @Inject(FileUploadService) public fileUploadService: FileUploadService
    ) {
        ApiConfig = apiConfig;
    }

    automaticallyCropTheImage(
        uId: string,
        minCropBoxWidth: number = 250,
        minCropBoxHeight: number = 375,
        isVisitor: boolean = false
    ): Observable<IAutoCropResultData> {
        let tmpDiv;
        let detectData;
        if (this.cropper) {
            this.cropper.destroy();
        }
        const detectUrl = isVisitor ? this.detectVisitorFace(uId) : this.detectFace(uId);
        return detectUrl.pipe(
            catchError(((errorData) => {
                if (errorData.status !== ResponseStatus.InternalServerError) {
                    errorData.error.modelState.validationError.system.forEach((message: string) => {
                        this.toasterService.error(message);
                    });
                }
                return throwError(errorData);
            })),
            filter((detectFaceData) => {
                detectData = detectFaceData;
                return detectFaceData?.settings.isImageRecognitionEnabled;
            }),
            switchMap(() => {
                return this.imageService.getBlobImageByUidFromS3(uId, isVisitor);
            }),
            switchMap((file => {
                return this.imageService.blobToImage(file);
            }))
        )
            .pipe(switchMap((htmlImageElement) => {
                const replaySubject$ = new ReplaySubject(1);
                tmpDiv = this.document.createElement(`div`);
                const body = this.document.querySelector('body');
                tmpDiv.style.float = 'left';
                // tslint:disable-next-line:max-line-length
                tmpDiv.classList = `badge-request-cropper-container direction-${htmlImageElement.width >= htmlImageElement.height ? 'row' : 'column'}`;
                tmpDiv.appendChild(htmlImageElement);
                body.appendChild(tmpDiv);

                this.cropper = new Cropper(htmlImageElement,
                    {
                        zoomable: true,
                        scalable: true,
                        aspectRatio: 2 / 3,
                        checkCrossOrigin: false,
                        ready: () => {
                            const cropperCanvasData = this.cropper.getCanvasData();
                            this.cropper.setCropBoxData(
                                this.calculateCropBoxData(
                                    this.cropper.getImageData(),
                                    cropperCanvasData,
                                    detectData.boundingBox
                                )
                            );
                            const cropBoxData = this.addPercentToCropBoxData(
                                this.cropper.getCropBoxData(),
                                this.cropper.getImageData(),
                                cropperCanvasData,
                                detectData.settings.boxingPercentage,
                            );
                            this.cropper.setCropBoxData(cropBoxData);
                            const localCanvas = this.cropper.getCroppedCanvas({
                                maxWidth: 600,
                                maxHeight: 600,
                                imageSmoothingQuality: 'high',
                            });
                            setTimeout(() => {
                                replaySubject$.next({
                                    croppedImageBase64: localCanvas.toDataURL('image/jpeg'),
                                    cropperData: {
                                        data: this.cropper.getData(),
                                        cropBoxData: this.cropper.getCropBoxData(),
                                        canvasData: cropperCanvasData
                                    },
                                    // tslint:disable-next-line:max-line-length
                                    isImageRecognitionEnabled: htmlImageElement.width >= minCropBoxWidth && htmlImageElement.height >= minCropBoxHeight
                                });

                                tmpDiv.remove();
                                this.cropper.destroy();
                                this.cropper = null;
                                replaySubject$.complete();
                            }, 0);
                        }
                    });
                return replaySubject$ as ReplaySubject<IAutoCropResultData>;
            }));
    }

    detectFace(uid: string): Observable<IDetectFacesDto> {
        return this.httpClient.get<IDetectFacesDto>(ApiConfig.ImageRekognition.detectFace.replace('{uid}', uid));
    }

    detectVisitorFace(uid: string): Observable<IDetectFacesDto> {
        return this.httpClient.get<IDetectFacesDto>(ApiConfig.ImageRekognition.detectVisitorFace.replace('{uid}', uid));
    }

    getSetting(): Observable<ICroppingSettings> {
        return this.httpClient.get<ICroppingSettings>(ApiConfig.ImageRekognition.autocroppingSettings);
    }

    calculateCropBoxData(imageData: ICropBoxViewData, canvasData: ICropBoxViewData, detectFaceData: ICropBoxViewData): ICropBoxViewData {

        const height = detectFaceData.height;
        const left = detectFaceData.left;
        const top = detectFaceData.top;
        const width = detectFaceData.width;

        return {
            height: imageData.height * height,
            left: canvasData.left + (imageData.width * left),
            top: canvasData.top + (imageData.height * top),
            width: imageData.width * width,
        };
    }

    addPercentToCropBoxData(
        cropBoxData: Cropper.CropBoxData,
        imageData: ICropBoxViewData,
        canvasData: ICropBoxViewData,
        boxingPercentage: number,
        ): Cropper.CropBoxData {

        const {maxHeight, maxWidth, height, width, left} = (this.cropper as any ).cropBoxData;

        if (height + ((height / 100) * boxingPercentage) > maxHeight) {
            return  {
                ...cropBoxData,
                height: maxHeight,
                left: left - ((maxWidth - width) / 2)
            };
        }

        const addedCropBoxWidth  = ((cropBoxData.width / 100)  * boxingPercentage);
        const addedCropBoxHeight = ((cropBoxData.height / 100) * boxingPercentage);

        const newCropBoxHeight = cropBoxData.height + addedCropBoxHeight;
        const newCropBoxWidth  = cropBoxData.width  + addedCropBoxWidth;

        const newLeft = cropBoxData.left - (addedCropBoxWidth / 2);
        const newTop  = cropBoxData.top  - (addedCropBoxHeight / 2);

        const calulatedHeight = newCropBoxHeight < canvasData.height ? newCropBoxHeight : canvasData.height;
        const calulatedWidth  = newCropBoxWidth  < canvasData.width  ? newCropBoxWidth : (canvasData.height / 3) * 2;

        return  {
            height: calulatedHeight - 0.00001,
            width: calulatedWidth - 0.00001,
            top: newTop > 0 ? newTop : 0,
            left: newLeft > 0 ? newLeft : 0,
        };
    }

    cropPhoto(response: ICroppPhotoRequestOld): Observable<{} | { error: HttpErrorResponse }> {
        return this.httpClient.post(ApiConfig.ImageRekognition.croppPhoto, response)
            .pipe(catchError(error => of({error})));
    }

    changeSizeAndCropPhoto(request: ICroppPhotoRequestOld, dataUrl: string, uploadUrl: string): Observable<any> {
        return this.imageService.changeImageSize(
            this.imageService.fromDataUrlToFile(dataUrl, 'tmpFilename'),
            this.imageService.imageDefaultType,
            600,
            EReturnImageResizeType.dataUrl,
            'tmpFilename'
        ).pipe(switchMap((base64: string) => {
            return this.fileUploadService
                .uploadFile(
                    this.imageService.fromDataUrlToFile(base64, 'tmpFilename', this.imageService.imageDefaultType),
                    uploadUrl
                ).pipe(switchMap((uploadResponse => this.cropPhoto({...request, uId: uploadResponse.uId}))));
        }));
    }
}
